import { Check, Camera, AlertTriangle, Scale } from "lucide-react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import CtaSection from "@/components/CtaSection";
import { JUNK_SERVICES, JUNK_PRICING_FACTORS, JUNK_SAFETY } from "@/data/site";

export default function JunkRemoval() {
  return (
    <>
      <Seo
        title="Junk Removal & Property Cleanouts Greensboro NC | Authentically Yours"
        description="Junk removal and property cleanouts in Greensboro, NC and surrounding areas — furniture, debris, apartment, garage and commercial cleanouts. Free estimate."
        path="/junk-removal"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Junk Removal", path: "/junk-removal" },
        ]}
      />
      <PageHero
        eyebrow="Junk Removal"
        title="Clear the Clutter. Reclaim Your Space."
        intro="Convenient junk removal and property cleanouts for homes, rentals, businesses, turnovers and more throughout Greensboro, NC and surrounding areas."
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Junk Removal", path: "/junk-removal" },
        ]}
      />

      <section className="container-x py-20 sm:py-28" aria-labelledby="junk-services-h">
        <SectionHeading eyebrow="What We Haul" title="Junk Removal & Cleanouts" as="h2" />
        <ul className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
          {JUNK_SERVICES.map((s, i) => (
            <Reveal as="li" key={s} delay={(i % 3) * 0.06} className="flex items-start gap-3 text-ink/80">
              <Check size={17} strokeWidth={2} className="mt-1 text-rose shrink-0" aria-hidden="true" />
              {s}
            </Reveal>
          ))}
        </ul>
      </section>

      {/* Pricing factors + photos */}
      <section className="bg-blush-soft/50 border-y border-gold/15" aria-labelledby="pricing-h">
        <div className="container-x py-20 sm:py-28 grid lg:grid-cols-2 gap-14 items-start">
          <div>
            <span className="inline-flex h-12 w-12 items-center justify-center border border-rose/30 text-rose mb-6" aria-hidden="true">
              <Scale size={22} strokeWidth={1.25} />
            </span>
            <h2 className="text-3xl sm:text-4xl text-ink">How Junk-Removal Pricing Works</h2>
            <p className="mt-4 text-ink/70 leading-relaxed max-w-lg">
              Every cleanout is different, so we quote each one individually. Pricing
              can depend on:
            </p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-2.5 max-w-lg">
              {JUNK_PRICING_FACTORS.map((f) => (
                <li key={f} className="flex items-start gap-3 text-ink/80 text-[0.95rem]">
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-rose shrink-0" aria-hidden="true" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
          <Reveal>
            <div className="bg-white border border-gold/25 p-8">
              <span className="inline-flex h-12 w-12 items-center justify-center border border-rose/30 text-rose mb-6" aria-hidden="true">
                <Camera size={22} strokeWidth={1.25} />
              </span>
              <h3 className="text-2xl text-ink">Send Photos for a Faster Quote</h3>
              <p className="mt-3 text-ink/70 leading-relaxed">
                Photos help us give you an accurate estimate quickly. You can attach
                images right on the estimate form — especially helpful for junk
                removal and cleanouts.
              </p>
              <Link to="/free-estimate" className="btn-primary mt-6" data-testid="junk-photo-cta">
                Get a Free Junk Removal Estimate
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="container-x py-12">
        <div className="flex items-start gap-4 border border-rose/30 bg-white p-6 max-w-3xl" role="note">
          <AlertTriangle size={22} strokeWidth={1.5} className="text-rose shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-sm text-ink/75 leading-relaxed">{JUNK_SAFETY}</p>
        </div>
      </section>

      <CtaSection
        title="Get a Free Junk Removal Estimate"
        text="Tell us what you'd like removed — add a few photos — and we'll get back to you about your free estimate."
        estimateLabel="Get a Free Estimate"
      />
    </>
  );
}
